import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/authentication/useAuth';

/**
 * LoginPage Component
 * Displays login page for users who need to authenticate
 * Uses MSAL redirect flow for Microsoft SSO (same tab)
 */
export default function LoginPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login, isAuthenticated, isLoading, error: authError, clearError, msalReady } = useAuth();
    const [localError, setLocalError] = useState(null);

    const errorParam = searchParams.get('error');
    const errorMessage = searchParams.get('message');

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated && !isLoading) {
            navigate('/flows/root');
        }
    }, [isAuthenticated, isLoading, navigate]);

    // Sync auth error to local state
    useEffect(() => {
        if (authError) {
            setLocalError(authError);
        }
    }, [authError]);

    const handleLogin = async () => {
        try {
            setLocalError(null);
            if (clearError) clearError();
            
            // This will redirect to Microsoft login page (same tab)
            await login();
            // Page will redirect, no code runs after this
        } catch (err) {
            console.error('Login failed:', err);
            setLocalError(err.message || 'Failed to login. Please try again.');
        }
    };

    const getErrorMessage = () => {
        if (localError) return localError;
        if (errorMessage) return decodeURIComponent(errorMessage);
        
        switch (errorParam) {
            case 'auth_failed':
                return 'Authentication failed. Please try again.';
            case 'no_code':
                return 'No authorization code received. Please try again.';
            case 'session_expired':
                return 'Your session has expired. Please sign in again.';
            default:
                return null;
        }
    };

    const displayError = getErrorMessage();
    const showLoading = isLoading || !msalReady;

    if (isLoading && !msalReady) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Initializing authentication...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
            <div className="bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
                <div className="text-center mb-8">
                    <img 
                        src="/abinbev logo.png" 
                        alt="AB InBev Logo" 
                        className="h-16 mx-auto mb-6"
                    />
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Welcome Back
                    </h1>
                    <p className="text-gray-400">
                        Sign in to access the Campaign ROI platform
                    </p>
                </div>

                {displayError && (
                    <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                            <svg 
                                className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                                />
                            </svg>
                            <span className="text-red-500 text-sm">
                                {displayError}
                            </span>
                        </div>
                    </div>
                )}

                <button
                    onClick={handleLogin}
                    disabled={showLoading}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {showLoading ? (
                        <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                            <span>Loading...</span>
                        </>
                    ) : (
                        <>
                            <svg 
                                className="w-5 h-5" 
                                viewBox="0 0 21 21" 
                                fill="currentColor"
                            >
                                <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
                            </svg>
                            <span>Sign in with Microsoft</span>
                        </>
                    )}
                </button>

                <div className="mt-6 text-center">
                    <button
                        onClick={() => navigate('/')}
                        className="text-gray-400 hover:text-white text-sm transition-colors"
                    >
                        ← Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
}
